import { useEffect, useState } from 'react';
import { Plus, Pencil, Briefcase } from 'lucide-react';
import { serviciosApi } from '../api/endpoints';
import { Modal } from '../components/ui/Modal';
import { Spinner } from '../components/ui/Spinner';
import { SearchInput } from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/EmptyState';
import { Alert } from '../components/ui/Alert';
import { useAuthStore } from '../auth/authStore';

const empty = { codigo: '', nombre: '', descripcion: '', precioBase: 0, categoria: '', unidad: 'Servicio' };

export function Servicios() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try { setData(await serviciosApi.list()); }
    catch (e) { setError(e?.response?.data?.error || 'Error'); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, precioBase: Number(form.precioBase) };
      if (editing) await serviciosApi.update(editing.id, { ...payload, activo: editing.activo });
      else await serviciosApi.create(payload);
      setModalOpen(false);
      await load();
    } catch (e) { setError(e?.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const filtered = (data ?? []).filter(s => !search || s.nombre.toLowerCase().includes(search.toLowerCase()) || s.codigo.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o código..." className="max-w-md" />
        {isAdmin && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg shrink-0">
            <Plus size={16} /> <span className="hidden sm:inline">Nuevo Servicio</span>
          </button>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {data === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Briefcase} title="Sin servicios" description="No hay servicios en el catálogo." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((s) => (
            <div key={s.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-[10px] font-mono text-cyan-400 tracking-wider">{s.codigo}</span>
                  <h3 className="font-semibold text-white mt-0.5">{s.nombre}</h3>
                  <span className="text-[10px] text-muted uppercase tracking-wide">{s.categoria || 'Sin categoría'}</span>
                </div>
                {isAdmin && (
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 opacity-0 group-hover:opacity-100">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted line-clamp-2 mb-3">{s.descripcion || 'Sin descripción'}</p>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-lg font-bold text-emerald-400">${Number(s.precioBase).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                <span className="text-[10px] text-muted">por {s.unidad}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Servicio' : 'Nuevo Servicio'} size="md">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Código *" value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} />
            <Field label="Categoría" value={form.categoria} onChange={(v) => setForm({ ...form, categoria: v })} />
            <Field label="Unidad" value={form.unidad} onChange={(v) => setForm({ ...form, unidad: v })} />
          </div>
          <Field label="Nombre *" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <Field label="Descripción" value={form.descripcion} onChange={(v) => setForm({ ...form, descripcion: v })} />
          <Field label="Precio base *" type="number" value={form.precioBase} onChange={(v) => setForm({ ...form, precioBase: v })} />
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border">
          <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">Cancelar</button>
          <button onClick={save} disabled={saving || !form.codigo || !form.nombre} className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">{label}</label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );
}
